import { Fragment, FunctionComponent, ReactNode } from "react";

import { TabChip } from "@/components/TabChip/TabChip";
import { classNames } from "@/utils/classNames";

import { FeedPageTab } from "@/pages/Private/redux/feed/feed.slice";

export interface FeedTabItem {
	value: FeedPageTab;
	label: string;
	count?: number;
	/** Renders a vertical divider before this tab (run tabs │ profile tabs). */
	divider?: boolean;
}

interface FeedTabsProps {
	tabs: FeedTabItem[];
	currentTab: FeedPageTab;
	onTabChange: (tab: FeedPageTab) => void;
	/** Slot rendered right after the first (New) tab — the jump-to-company trigger (D3). */
	jumpSlot?: ReactNode;
}

/** Level-2 tab strip of the Feed page, mirroring the list-detail `ListStatusTabStrip`. */
export const FeedTabs: FunctionComponent<FeedTabsProps> = ({
	tabs,
	currentTab,
	onTabChange,
	jumpSlot,
}) => (
	<div className="flex items-center h-[34px] px-2 bg-white border-b border-border-light overflow-x-auto">
		{tabs.map((tab, index) => {
			const isActive = currentTab === tab.value;

			return (
				<Fragment key={tab.value}>
					{tab.divider && <span className="mx-2 h-4 w-px shrink-0 bg-border-light" />}
					<button
						className={classNames(
							"cursor-pointer flex items-center gap-1.5 h-[32px] px-3 text-xs whitespace-nowrap border-b-2 -mb-px transition-colors",
							isActive
								? "text-text font-semibold border-blue-action"
								: "text-text-meta font-medium border-transparent hover:text-text"
						)}
						data-testid={`feed-tab-${tab.value}`}
						type="button"
						onClick={() => onTabChange(tab.value)}
					>
						<span>{tab.label}</span>
						{tab.count !== undefined && <TabChip active={isActive} label={`${tab.count}`} />}
					</button>
					{index === 0 && jumpSlot}
				</Fragment>
			);
		})}
	</div>
);
